import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Switch from "react-switch";
import "../modeSelect/modeSelect.css";
import "./sessionSettings.css";
import { fetchSession, saveSession, selectSession } from "../../sessionBuilderSlice";
import { AUTO_MAX_POINTS, AUTO_MIN_POINTS } from "../../../functions/scoring";

const SessionSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const session = useSelector(selectSession);

  const [scoringMode, setScoringMode] = useState("custom");
  const [pointValue, setPointValue] = useState(10);
  const [sessionMinutes, setSessionMinutes] = useState(10);
  const [pollDisplayMode, setPollDisplayMode] = useState("live");
  const [quizDisplayMode, setQuizDisplayMode] = useState("live");
  const [wordcloudLimit, setWordcloudLimit] = useState(3);
  const [pindropLimit, setPindropLimit] = useState(1);
  const [selfpacedLbVisible, setSelfpacedLbVisible] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchSession());
  }, [dispatch]);

  useEffect(() => {
    if (!session) return;
    setScoringMode(session.scoring_mode ?? "custom");
    setPointValue(session.point_value ?? 10);
    setSessionMinutes(Math.max(1, Math.round((session.session_duration ?? 600) / 60)));
    setPollDisplayMode(session.poll_display_mode ?? "live");
    setQuizDisplayMode(session.quiz_display_mode ?? "live");
    setWordcloudLimit(session.wordcloud_limit ?? 3);
    setPindropLimit(session.pindrop_limit ?? 1);
    setSelfpacedLbVisible(!!session.selfpaced_leaderboard_visible);
  }, [session]);

  const isLive = (session?.mode ?? "live") === "live";

  const handleSave = async () => {
    const pv = parseInt(pointValue, 10);
    if (scoringMode === "custom" && (Number.isNaN(pv) || pv < 1 || pv > 50)) {
      Swal.fire("", "Points per correct answer must be between 1 and 50", "warning");
      return;
    }
    setSaving(true);
    try {
      await dispatch(
        saveSession({
          mode: session?.mode ?? "live",
          scoring_mode: scoringMode,
          point_value: Number.isNaN(pv) ? 10 : pv,
          session_duration: Math.max(1, parseInt(sessionMinutes, 10) || 10) * 60,
          poll_display_mode: pollDisplayMode,
          quiz_display_mode: quizDisplayMode,
          wordcloud_limit: Math.min(10, Math.max(1, parseInt(wordcloudLimit, 10) || 1)),
          pindrop_limit: Math.min(10, Math.max(1, parseInt(pindropLimit, 10) || 1)),
          selfpaced_leaderboard_visible: selfpacedLbVisible ? 1 : 0,
        })
      ).unwrap();
      navigate("/admin/session/activities");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="rules-page-new">
        <div className="admin-tabs-holder-v2">
          <div className="tabs-main-title">
            Settings
            <span className="mode-pill">{isLive ? "Live" : "Self-Paced"}</span>
          </div>

          {/* Scoring */}
          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-star"></i> &nbsp; Scoring
            </div>
            <div className="form-group-v1">
              <label>Scoring mode</label>
              <select
                className="rules-grid-select"
                value={scoringMode}
                onChange={(e) => setScoringMode(e.target.value)}
              >
                <option value="auto">Auto (time-based)</option>
                <option value="custom">Custom (fixed points)</option>
              </select>
              <div className="code">
                {scoringMode === "auto"
                  ? `Correct quiz/pin-drop answers score between ${AUTO_MIN_POINTS} and ${AUTO_MAX_POINTS} points, scaled by how fast they were answered — ${AUTO_MAX_POINTS} for an instant answer, decaying to ${AUTO_MIN_POINTS} right as the timer runs out. A correct answer always scores more than an incorrect one, which always scores 0.`
                  : "Every correct answer scores the fixed point value below, no matter how long it took."}
              </div>
            </div>

            {scoringMode === "custom" && (
              <div className="form-group-v1">
                <label>Points per correct quiz answer (applies to every correct answer)</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={pointValue}
                  onChange={(e) => setPointValue(e.target.value)}
                />
                <div className="code">Between 1 and 50 points.</div>
              </div>
            )}
            {/* Complete-session timer removed from the UI — timing is per-question
                (set on each activity). A default value is still persisted so the
                stored setting stays valid. */}
          </div>

          {/* Live-only activity settings */}
          {isLive && (
            <div className="admin-tab-container">
              <div className="admin-tab-title">
                <i className="fa-solid fa-gear"></i> &nbsp; Activity Settings (Live)
              </div>

              <div className="form-group-v1">
                <label>Quiz result display</label>
                <select
                  className="rules-grid-select"
                  value={quizDisplayMode}
                  onChange={(e) => setQuizDisplayMode(e.target.value)}
                >
                  <option value="live">Live Bars (update as answers come in)</option>
                  <option value="presenter_reveal">Presenter Reveal (hidden until revealed)</option>
                </select>
                <div className="code">Controls whether quiz answer bars update live during the question or stay hidden until you reveal.</div>
              </div>

              <div className="form-group-v1">
                <label>Polling result display</label>
                <select
                  className="rules-grid-select"
                  value={pollDisplayMode}
                  onChange={(e) => setPollDisplayMode(e.target.value)}
                >
                  <option value="live">Live Results (update as votes come in)</option>
                  <option value="presenter_reveal">Presenter Reveal (hidden until revealed)</option>
                </select>
              </div>

              <div className="form-group-v1">
                <label>Word Cloud — responses allowed per participant</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={wordcloudLimit}
                  onChange={(e) => setWordcloudLimit(e.target.value)}
                />
                <div className="code">Between 1 and 10.</div>
              </div>

              <div className="form-group-v1">
                <label>Pin Drop — pins allowed per participant</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={pindropLimit}
                  onChange={(e) => setPindropLimit(e.target.value)}
                />
                <div className="code">Between 1 and 10.</div>
              </div>
            </div>
          )}

          {/* Self-paced-only */}
          {!isLive && (
            <div className="admin-tab-container">
              <div className="admin-tab-title">
                <i className="fa-solid fa-ranking-star"></i> &nbsp; Leaderboard (Self-Paced)
              </div>
              <div className="setting-toggle-single">
                <div className="form-group-v2">
                  <label>Show leaderboard to participants</label>
                  <Switch checked={selfpacedLbVisible} onChange={setSelfpacedLbVisible} />
                </div>
              </div>
            </div>
          )}

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save & Continue"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SessionSettings;
